"use client";

import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { withFrontendOrigin } from "@/lib/apiHeaders";
import { authFetch, parseAuthJson } from "@/lib/authClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

type Props = {
  initialEmail?: string;
};

export default function ResendEmailButton({ initialEmail = "" }: Props) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);

  const handleResendVerification = async () => {
    const clean = (email || "").trim().toLowerCase();
    if (!clean) {
      toast.error("Inserisci la tua email");
      return;
    }

    try {
      setLoading(true);
      const res = await authFetch("/auth/resend-verification", {
        method: "POST",
        headers: withFrontendOrigin({ "Content-Type": "application/json" }),
        body: JSON.stringify({ email: clean }),
        cache: "no-store",
      });

      const data = await parseAuthJson<{ error?: string; message?: string }>(res);
      if (!res.ok) throw new Error(data?.error || "Errore invio email");

      // Messaggio uniforme lato server: ok anche se l'email non esiste
      toast.success(data?.message || "Email di verifica inviata.");
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Errore durante l'invio.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Trigger come link sotto i campi, stesso stile del forgot password */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="cursor-pointer text-sm text-card-foreground hover:underline"
      >
        Non hai ricevuto l’email di conferma?
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-card-foreground mt-2">
              Ottieni un nuovo link di verifica
            </DialogTitle>
          </DialogHeader>

          <div className="flex justify-start">
            <Input
              type="email"
              placeholder="Inserisci la tua email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button
              onClick={() => setOpen(false)}
              className="cursor-pointer"
              variant="secondary"
              type="button"
            >
              Annulla
            </Button>
            <Button
              onClick={handleResendVerification}
              disabled={loading}
              className="bg-(--btn-bg) text-(--btn-foreground) cursor-pointer rounded-lg px-6 py-2 text-md font-semibold transition-all duration-300 hover:bg-(--btn-hover)"
              type="button"
            >
              {loading ? "Invio..." : "Invia link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
